import { formatDate } from 'date-fns/format';
import useUpdateSubscriptionMutation from '@/api/mutations/useUpdateSubscriptionMutation';
import useGetBillingInfoQuery from '@/api/queries/useGetBillingInfoQuery';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { isBillingAddressComplete } from '@/models/Billing';
import { formatPlanCode, isFreePlan } from '@/modules/billing/models/Plan';
import {
  BillingDetailsEditPath,
  BillingInvoiceListPath,
  BillingPaymentMethodListPath,
  BillingUpgradePlanPaymentSetupPath,
} from '@/modules/billing/paths';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const BillingHome = () => {
  const navigate = useNavigate();
  const { billingInfo, isLoading: isLoadingBilling, error } = useGetBillingInfoQuery();
  const updateSubscription = useUpdateSubscriptionMutation();
  const handleDowngradeToFreePlan = async () => {
    try {
      await updateSubscription.mutateAsync({
        plan: 'free',
      });

      toast.custom((t) => (
        <CustomToast id={t.id} message={'Plan downgraded to Free.'} visible={t.visible} />
      ));
    } catch (downgradeError) {
      console.error('Error downgrading plan:', downgradeError);
    }
  };

  const handleUpgradeToPaidPlan = async () => {
    if (!isBillingAddressComplete(billingInfo?.billingDetails?.address)) {
      navigate(BillingDetailsEditPath);
      return;
    }

    const hasDefaultActivePaymentMethod = Boolean(
      billingInfo?.defaultPaymentMethod?.card?.isDefault &&
      billingInfo?.defaultPaymentMethod?.card?.isActive,
    );

    if (!hasDefaultActivePaymentMethod) {
      navigate(BillingUpgradePlanPaymentSetupPath);
      return;
    }

    try {
      await updateSubscription.mutateAsync({
        plan: 'paid',
        billingCycle: billingInfo?.subscription?.billingCycle ?? 'monthly',
      });

      toast.custom((t) => (
        <CustomToast id={t.id} message={'Plan upgraded successfully!'} visible={t.visible} />
      ));
    } catch (upgradeError) {
      console.error('Error upgrading plan:', upgradeError);
    }
  };

  if (isLoadingBilling) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (error) {
    return (
      <>
        <SettingsHeader title="Billing" icon={<CreditCardIcon className="w-6 h-6" />} />
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">
          Failed to load billing information.
        </div>
      </>
    );
  }

  const planName = formatPlanCode(billingInfo?.plan?.code);
  const isFree = isFreePlan(billingInfo?.plan?.code);
  const paidPlanActive = !isFree && Boolean(billingInfo?.subscription?.isActive);
  const paymentMethod = billingInfo?.defaultPaymentMethod?.card;
  const companyName = billingInfo?.billingDetails?.companyName || 'Not provided';
  const billingEmail = billingInfo?.billingDetails?.billingEmail || 'Not provided';
  const countryCode = billingInfo?.billingDetails?.address?.countryCode || 'Country not provided';
  const hasCompleteBillingAddress = isBillingAddressComplete(billingInfo?.billingDetails?.address);
  const periodStart = billingInfo?.subscription?.currentPeriod?.start;
  const periodEnd = billingInfo?.subscription?.currentPeriod?.end;
  const hasPeriodRange = Boolean(periodStart && periodEnd);
  const periodRange =
    hasPeriodRange && periodStart && periodEnd
      ? `${formatDate(periodStart, 'd MMM yyyy')} - ${formatDate(periodEnd, 'd MMM yyyy')}`
      : null;

  return (
    <>
      <SettingsHeader title="Billing" icon={<CreditCardIcon className="w-6 h-6" />} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Current plan (full width) */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <div className="text-sm font-medium text-slate-500">Current plan</div>

          <div
            className="mt-1 text-xl font-semibold text-slate-900"
            data-testid="billing-current-plan-name"
          >
            {planName}
          </div>

          <div className="mt-10">
            {isFree ? (
              <Button
                onClick={handleUpgradeToPaidPlan}
                loading={updateSubscription.isPending}
                data-testid="billing-upgrade-button"
                className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white hover:bg-slate-800"
              >
                Upgrade
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="text-sm text-slate-600" data-testid="billing-paid-plan-status">
                  You’re on a paid plan.
                </div>
                {paidPlanActive && periodRange && (
                  <div className="text-sm text-slate-700" data-testid="billing-paid-plan-period">
                    Upcoming cycle: <span className="font-medium">{periodRange}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-slate-500">Invoices</div>
              <div className="mt-2 text-sm text-slate-700">
                Review your past and current subscription invoices.
              </div>
            </div>

            <Button to={BillingInvoiceListPath} variant="secondary">
              View invoices
            </Button>
          </div>
        </div>

        {/* Payment method */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-slate-500">Payment method</div>

              {paymentMethod ? (
                <div className="mt-2" data-testid="billing-payment-method-summary">
                  <div
                    className="text-sm font-semibold text-slate-900"
                    data-testid="billing-payment-method-card"
                  >
                    {paymentMethod.brand} •••• {paymentMethod.last4}
                  </div>
                  <div className="mt-1 text-sm text-slate-600">
                    Expires {paymentMethod.expMonth}/{paymentMethod.expYear}
                  </div>
                </div>
              ) : (
                <div
                  className="mt-2 text-sm text-slate-700"
                  data-testid="billing-no-payment-method"
                >
                  No payment method on file.
                  <div className="mt-1 text-xs text-slate-500">Required when you upgrade.</div>
                </div>
              )}
            </div>

            <Button
              to={BillingPaymentMethodListPath}
              variant="secondary"
              data-testid="billing-manage-payment-methods-button"
            >
              Manage
            </Button>
          </div>
        </div>

        {/* Billing details */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-slate-500">Billing details</div>
              <div className="mt-2 text-sm font-medium text-slate-900">{companyName}</div>
              <div className="text-sm text-slate-600">{billingEmail}</div>
              <div className="mt-1 text-xs text-slate-500">{countryCode}</div>
              {!hasCompleteBillingAddress && (
                <div className="mt-2 text-xs font-medium text-amber-700">
                  Add your billing address before upgrading to a paid plan.
                </div>
              )}
            </div>

            <Button to={BillingDetailsEditPath} variant="secondary">
              Edit
            </Button>
          </div>
        </div>
      </div>

      {!isFree && (
        <div className="mt-6 flex items-center justify-between gap-4 border-t border-slate-200 pt-6">
          <div className="text-sm text-slate-500">
            Switching to Free downgrades your plan at the end of the current billing cycle.
          </div>

          <Button
            variant="secondary"
            onClick={handleDowngradeToFreePlan}
            loading={updateSubscription.isPending}
            data-testid="billing-downgrade-button"
          >
            Switch to Free
          </Button>
        </div>
      )}
    </>
  );
};

export default BillingHome;
